import { type QueryClient, queryOptions } from "@tanstack/vue-query";
import { useCacheTimings } from "~/composables/cacheTiming";
import {
  type AccessibleEntity,
  Chapter,
  type ChapterEntity,
  type ChapterListParams,
  type EntityAccessEntity,
  type IncludeArr,
  isExpanded,
} from "~/src/api";
import { checkEntityAccess } from "~/src/gating";
import { type AuthFunction, toUnordered } from "~/utils/tanstack/global";

export const getChapterFromCache = (
  client: () => QueryClient,
  chapterId: string,
  includes: IncludeArr<ChapterEntity> = [],
  authed = false,
) => {
  const qc = client();
  const cache = qc.getQueryCache();
  return cache.find({
    queryKey: [
      "chapter",
      "entity",
      chapterId,
      {
        includes: toUnordered("includes", includes),
        authed: authed || undefined,
      },
    ],
    exact: false,
  })?.state.data as ChapterEntity | undefined;
};

export const cacheChapter = (
  client: () => QueryClient,
  chapter: ChapterEntity,
  authed = false,
) => {
  const qc = client();
  const expandedRelations = new Set(
    chapter.relationships.filter(isExpanded).map((rel) => rel.type),
  );

  qc.setQueryData(
    [
      "chapter",
      "entity",
      chapter.id,
      {
        includes: toUnordered("includes", Array.from(expandedRelations)),
        authed: authed || undefined,
      },
    ],
    chapter,
  );
};

export const useChapterList = (
  params: MaybeRefOrGetter<ChapterListParams> = {},
  auth?: AuthFunction,
) => {
  const unorderedParams = computed(() => {
    const paramsValue = toValue(params);
    return {
      ...paramsValue,
      includes: paramsValue.includes
        ? toUnordered("includes", paramsValue.includes)
        : undefined,
      translatedLanguages: paramsValue.translatedLanguages
        ? toUnordered("langs", paramsValue.translatedLanguages)
        : undefined,
      ids: paramsValue.ids ? toUnordered("ids", paramsValue.ids) : undefined,
      titleIds: paramsValue.titleIds
        ? toUnordered("titleIds", paramsValue.titleIds)
        : undefined,
      states: paramsValue.states
        ? toUnordered("states", paramsValue.states)
        : undefined,
      authed: !!auth || undefined,
    };
  });

  return queryOptions({
    queryKey: ["chapter", "collection", unorderedParams],
    queryFn: async ({ client }) => {
      const paramsValue = toValue(params);
      const res = await Chapter.search(
        paramsValue,
        (await auth?.()) || undefined,
      );
      // cache for individual entities returned by the list endpoint
      res.data.forEach((chapter) =>
        cacheChapter(() => client as QueryClient, chapter, !!auth),
      );
      return res;
    },
    staleTime: useCacheTimings().collection.chapter,
  });
};

export const useChapterGet = (
  id: MaybeRefOrGetter<string>,
  includes: IncludeArr<ChapterEntity> = [],
  auth?: AuthFunction,
) => {
  return queryOptions({
    queryKey: [
      "chapter",
      "entity",
      id,
      {
        includes: toUnordered("includes", includes),
        authed: !!auth || undefined,
      },
    ],
    queryFn: async () => {
      const idValue = toValue(id);
      if (!idValue) throw new Error("Chapter Id unspecified");
      if (idValue === "unknown") throw new Error("Chapter Unknown");
      return await Chapter.get(
        idValue,
        includes,
        (await auth?.()) || undefined,
      );
    },
    staleTime: useCacheTimings().entity.chapter,
  });
};

export const useChapterPageGet = (
  id: MaybeRefOrGetter<string>,
  auth?: AuthFunction,
) => {
  return queryOptions({
    queryKey: [
      "chapter_pages",
      "cdn_urls",
      id,
      { authed: !!auth || undefined },
    ],
    queryFn: async () => {
      const idValue = toValue(id);
      return await Chapter.getPageUrls(idValue, (await auth?.()) || undefined);
    },
    staleTime: useCacheTimings().cdn_urls.chapter_pages,
  });
};

export const useChapterAccess = (
  ids: MaybeRef<string[]>,
  entityType: AccessibleEntity["type"],
  auth?: AuthFunction,
) => useGatingAccess(ids, entityType, auth);

export const useAuthedChapterAccess = (
  ids: string[],
  entityType: AccessibleEntity["type"],
) => {
  const queryKey = Object.fromEntries(ids.map((id) => [id, id]));
  return queryOptions({
    queryKey: ["entity_access_map", "entity", queryKey, "authed"],
    queryFn: async () => {
      if (!ids.length)
        return {
          id: "",
          type: "entity_access_map",
          attributes: { map: {} },
        } as EntityAccessEntity;

      const token = await getTokenOrThrow();
      return await checkEntityAccess(
        ids.map((id) => ({
          entityId: id,
          entityType,
        })),
        token,
      );
    },
    select: (data) => data.attributes.map,
    throwOnError: false,
    // limit caching on this
    // staleTime: useCacheTimings().entity.entity_access_map,
    staleTime: 1000 * 60,
  });
};
